import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { HelmetProvider } from "react-helmet-async";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { installDomMutationGuards } from "../lib/dom-mutation-guards";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import TrackingScripts from "@/components/TrackingScripts";
import { PageTransition } from "@/components/motion/PageTransition";
import { SiteLayout } from "@/components/site/SiteLayout";

function NotFoundComponent() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const payload = JSON.stringify({
        url: window.location.pathname + window.location.search,
        referrer: document.referrer || "",
        userAgent: navigator.userAgent,
      });
      // Fire-and-forget; keepalive lets the request survive navigation.
      fetch("/api/public/log-404", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* noop */
    }
  }, []);

  return (
    <SiteLayout>
      <div className="flex min-h-[60vh] items-center justify-center bg-background px-4 py-20">
        <div className="max-w-md text-center">
          <h1 className="text-7xl font-bold text-foreground">404</h1>
          <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The page you're looking for doesn't exist or has been moved.
          </p>
          <div className="mt-6">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Go home
            </Link>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}


function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // Per-page title/description/OG live in each route's PageSEO (Helmet).
      // Intentionally no default <title> or <meta name="description"> here —
      // emitting them at the root produces duplicate tags on every SSR page
      // because Helmet appends its own, and crawlers see two <title>s.
      { name: "author", content: "Comprehensive Tax Resolution LLC" },
      { name: "google-site-verification", content: "7bS8l0DAtfm1A31C1GlIDoUhRGUuQiVMJOOTfueNmRQ" },
      { name: "theme-color", content: "#0a2540" },
      // No sitewide title/description/OG here — every route uses PageSEO
      // (Helmet) which manages its own tags. Emitting og:title/og:type/
      // og:site_name/twitter:card here would produce duplicates on every
      // SSR page because HeadContent and Helmet write to <head>
      // independently and Helmet only dedupes its own tags.
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      // Perf: warm up third-party connections used on most pages so DNS/TLS
      // happen in parallel with HTML parsing instead of blocking later requests.
      { rel: "preconnect", href: "https://cdn.callrail.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://images.unsplash.com" },
      { rel: "dns-prefetch", href: "https://cdn.callrail.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://connect.facebook.net" },
      { rel: "dns-prefetch", href: "https://bat.bing.com" },
      { rel: "dns-prefetch", href: "https://api.autodrivecrm.com" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/* Third-party DOM mutators are loaded only after React has hydrated.
            CallRail and Cognos rewrite text nodes/links; if they run before
            hydration, React sees swapped phone numbers and aborts navigation. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){function ready(cb){var done=false;function run(){if(done)return;done=true;cb();}function loaded(){if(document.readyState==='complete'){setTimeout(run,0);}else{window.addEventListener('load',function(){setTimeout(run,0);},{once:true});}}if(window.__ctrHydrated){loaded();return;}window.addEventListener('ctr:hydrated',loaded,{once:true});setTimeout(loaded,8000);}window.__ctrAfterHydration=ready;})();`,
          }}
        />
        {/* The external bootstrap keeps OTTO identifiers out of the document's
            inline source, preventing its scanner from counting the loader and
            the installed pixel as two separate integrations. */}
        <script src="/otto-loader.js" />

        {/* CallRail: swap.js observes mutations, so post-hydration loading
            still swaps numbers while preserving React's initial hydration. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){window.__ctrAfterHydration(function(){var s='//cdn.callrail.com/companies/366141248/3de053469fc0df38e7db/12/swap.js';if(document.querySelector('script[src*="cdn.callrail.com/companies/366141248"]'))return;var t=document.createElement('script');t.async=true;t.src=s;document.head.appendChild(t);});})();`,
          }}
        />
        {/* GHL external tracking: attribution stitching (UTMs, referrer, gclid/fbclid)
            so contacts created via the API show full source attribution + page-view
            timeline in GoHighLevel. Deferred until after load to protect LCP/TBT. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){window.__ctrAfterHydration(function(){var src='https://api.autodrivecrm.com/js/external-tracking.js';var id='tk_c0c76f5ee6b8435480b214ad6d2c9248';if(document.querySelector('script[src="'+src+'"]'))return;var t=document.createElement('script');t.async=true;t.src=src;t.setAttribute('data-tracking-id',id);document.head.appendChild(t);});})();`,
          }}
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    installDomMutationGuards();
    const w = window as Window & { __ctrHydrated?: boolean };
    w.__ctrHydrated = true;
    window.dispatchEvent(new Event("ctr:hydrated"));
  }, []);


  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <TrackingScripts />
          <Toaster />
          <Sonner />
          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <PageTransition>
            <Outlet />
          </PageTransition>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
